/**
 * Privacy Opt-Out Manager
 * Handles GPC detection, localStorage opt-out management, and Shopify consent API integration
 * CCPA/US State Privacy Law compliant
 */
(() => {
  'use strict';

  const STORAGE_KEY = 'privacy_opt_out';
  const OPT_OUT_EVENT = 'privacyOptOutReady';
  const OPT_OUT_CHANGE_EVENT = 'privacyOptOutChange';

  const PrivacyOptOut = {
    customerId: null,
    isOptedOut: false,
    hasGPCSignal: false,
    shopifyOptOut: false,

    /**
     * Initialize the privacy opt-out manager
     * @param {string|null} customerId - Shopify customer ID if logged in
     */
    init(customerId) {
      this.customerId = customerId;
      this.hasGPCSignal = this.detectGPC();

      // Clear localStorage if customer changed (logged out or different user)
      this.clearIfCustomerChanged(customerId);

      this.shopifyOptOut = this.getShopifyOptOutStatus();

      // Sync from server tag if customer is tagged but localStorage doesn't reflect it
      if (window.__customerPrivacy && window.__customerPrivacy.hasPrivacyTag && !this.getOptOutStatus()) {
        console.log('[PrivacyOptOut] Server tag detected, syncing to localStorage');
        this.setOptOut(true, 'server_tag');
      }

      // Check localStorage, GPC, and Shopify customer privacy status
      this.isOptedOut = this.getOptOutStatus() || this.shopifyOptOut;

      // Auto opt-out if GPC signal is detected
      if (this.hasGPCSignal && !this.isOptedOut) {
        this.setOptOut(true, 'gpc');
      }

      // Sync with Shopify's customerPrivacy API if available
      this.syncWithShopifyConsent();

      // Dispatch ready event
      this.dispatchEvent(OPT_OUT_EVENT, {
        isOptedOut: this.isOptedOut,
        hasGPCSignal: this.hasGPCSignal,
        shopifyOptOut: this.shopifyOptOut,
        customerId: this.customerId
      });

      // Bind form handlers if on privacy page
      this.bindFormHandlers();

      console.log('[PrivacyOptOut] Initialized', {
        isOptedOut: this.isOptedOut,
        hasGPCSignal: this.hasGPCSignal,
        shopifyOptOut: this.shopifyOptOut,
        customerId: this.customerId
      });

      // Log initial Shopify consent state for debugging
      this.verifyShopifyConsent();
    },

    /**
     * Clear localStorage if a DIFFERENT customer logs in
     * Keeps preferences when: same user logs out/in, or guest browsing
     * @param {string|null} currentCustomerId - Current customer ID
     */
    clearIfCustomerChanged(currentCustomerId) {
      try {
        const storedData = this.getOptOutData();
        if (!storedData) return;

        const storedCustomerId = storedData.customerId;

        // Only clear if a DIFFERENT user is now logged in
        // Don't clear if:
        // - Same user logged back in (currentCustomerId === storedCustomerId)
        // - User logged out (currentCustomerId is null) - keep preference for when they log back in
        // - Guest opted out (storedCustomerId is null) - keep guest preference
        if (currentCustomerId !== null &&
            storedCustomerId !== null &&
            storedCustomerId !== currentCustomerId) {
          console.log('[PrivacyOptOut] Different customer logged in, clearing stored preferences');
          console.log('  - Stored customer:', storedCustomerId);
          console.log('  - Current customer:', currentCustomerId);
          localStorage.removeItem(STORAGE_KEY);
          localStorage.removeItem(STORAGE_KEY + '_request');
        } else {
          console.log('[PrivacyOptOut] Keeping stored preferences', {
            storedCustomerId,
            currentCustomerId,
            isOptedOut: storedData.optedOut
          });
        }
      } catch (e) {
        console.error('[PrivacyOptOut] Error checking customer change:', e);
      }
    },

    /**
     * Detect Global Privacy Control (GPC) signal
     * @returns {boolean}
     */
    detectGPC() {
      return navigator.globalPrivacyControl === true;
    },

    /**
     * Get Shopify customer privacy opt-out status
     * Checks if customer has opted out via Shopify's customerPrivacy API
     * @returns {boolean}
     */
    getShopifyOptOutStatus() {
      try {
        // Check Shopify's customerPrivacy API
        if (typeof window.Shopify !== 'undefined' && window.Shopify.customerPrivacy) {
          // saleOfDataAllowed returns true if sale IS allowed (not opted out)
          // So we invert it: if sale is NOT allowed, customer has opted out
          if (typeof window.Shopify.customerPrivacy.saleOfDataAllowed === 'function') {
            const saleAllowed = window.Shopify.customerPrivacy.saleOfDataAllowed();
            if (saleAllowed === false) {
              console.log('[PrivacyOptOut] Shopify customerPrivacy: sale of data not allowed');
              return true;
            }
          }

          // Also check current visitor consent
          if (typeof window.Shopify.customerPrivacy.currentVisitorConsent === 'function') {
            const consent = window.Shopify.customerPrivacy.currentVisitorConsent();
            if (consent && consent.saleOfData === 'no') {
              console.log('[PrivacyOptOut] Shopify visitor consent: sale of data opted out');
              return true;
            }
          }
        }
      } catch (e) {
        console.error('[PrivacyOptOut] Error checking Shopify opt-out status:', e);
      }
      return false;
    },

    /**
     * Check if user is logged in
     * @returns {boolean}
     */
    isLoggedIn() {
      return window.__customerPrivacy && window.__customerPrivacy.isLoggedIn === true;
    },

    /**
     * Get customer info from Liquid template
     * @returns {Object|null}
     */
    getCustomerInfo() {
      if (window.__customerPrivacy && window.__customerPrivacy.isLoggedIn) {
        return {
          id: window.__customerPrivacy.customerId,
          email: window.__customerPrivacy.email,
          firstName: window.__customerPrivacy.firstName,
          lastName: window.__customerPrivacy.lastName
        };
      }
      return null;
    },

    /**
     * Get current opt-out status from localStorage
     * @returns {boolean}
     */
    getOptOutStatus() {
      try {
        const stored = localStorage.getItem(STORAGE_KEY);
        if (stored) {
          const data = JSON.parse(stored);
          return data.optedOut === true;
        }
      } catch (e) {
        console.error('[PrivacyOptOut] Error reading opt-out status:', e);
      }
      return false;
    },

    /**
     * Get full opt-out data from localStorage
     * @returns {Object|null}
     */
    getOptOutData() {
      try {
        const stored = localStorage.getItem(STORAGE_KEY);
        if (stored) {
          return JSON.parse(stored);
        }
      } catch (e) {
        console.error('[PrivacyOptOut] Error reading opt-out data:', e);
      }
      return null;
    },

    /**
     * Set opt-out status
     * @param {boolean} optOut - Whether to opt out
     * @param {string} source - Source of the opt-out (form, gpc, api)
     * @returns {boolean} Success status
     */
    setOptOut(optOut, source = 'form') {
      try {
        const data = {
          optedOut: optOut,
          timestamp: new Date().toISOString(),
          source: source,
          customerId: this.customerId,
          gpcDetected: this.hasGPCSignal
        };

        localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
        this.isOptedOut = optOut;

        // Sync with Shopify consent API
        this.syncWithShopifyConsent();

        // Dispatch change event
        this.dispatchEvent(OPT_OUT_CHANGE_EVENT, {
          isOptedOut: this.isOptedOut,
          source: source
        });

        console.log('[PrivacyOptOut] Opt-out status updated:', data);
        return true;
      } catch (e) {
        console.error('[PrivacyOptOut] Error setting opt-out status:', e);
        return false;
      }
    },

    /**
     * Clear opt-out status (opt back in)
     * @returns {boolean} Success status
     */
    clearOptOut() {
      return this.setOptOut(false, 'form');
    },

    /**
     * Sync with Shopify's customerPrivacy API
     */
    syncWithShopifyConsent(retryCount = 0) {
      const MAX_RETRIES = 5;
      const RETRY_DELAY = 500; // ms

      if (typeof window.Shopify !== 'undefined' && window.Shopify.customerPrivacy) {
        try {
          // Set tracking consent including sale_of_data
          if (typeof window.Shopify.customerPrivacy.setTrackingConsent === 'function') {
            window.Shopify.customerPrivacy.setTrackingConsent({
              marketing: !this.isOptedOut,
              analytics: !this.isOptedOut,
              preferences: true,
              sale_of_data: !this.isOptedOut
            }, () => {
              console.log('[PrivacyOptOut] Shopify setTrackingConsent completed:', {
                sale_of_data: !this.isOptedOut,
                marketing: !this.isOptedOut,
                analytics: !this.isOptedOut
              });

              // Verify the consent was set
              this.verifyShopifyConsent();
            });
          }
        } catch (e) {
          console.error('[PrivacyOptOut] Error syncing with Shopify consent:', e);
        }
      } else if (retryCount < MAX_RETRIES) {
        console.warn('[PrivacyOptOut] Shopify customerPrivacy API not available, retrying (' + (retryCount + 1) + '/' + MAX_RETRIES + ')...');
        setTimeout(() => {
          this.syncWithShopifyConsent(retryCount + 1);
        }, RETRY_DELAY * (retryCount + 1));
      } else {
        console.error('[PrivacyOptOut] Shopify customerPrivacy API not available after ' + MAX_RETRIES + ' retries');
      }
    },

    /**
     * Verify Shopify consent state (for debugging)
     */
    verifyShopifyConsent() {
      if (typeof window.Shopify !== 'undefined' && window.Shopify.customerPrivacy) {
        try {
          const saleAllowed = window.Shopify.customerPrivacy.saleOfDataAllowed?.() ?? 'N/A';
          const currentConsent = window.Shopify.customerPrivacy.currentVisitorConsent?.() ?? {};
          const region = window.Shopify.customerPrivacy.getRegion?.() ?? 'N/A';
          const inSaleRegion = window.Shopify.customerPrivacy.saleOfDataRegion?.() ?? 'N/A';

          console.log('[PrivacyOptOut] Shopify consent verification:', {
            saleOfDataAllowed: saleAllowed,
            currentVisitorConsent: currentConsent,
            region: region,
            saleOfDataRegion: inSaleRegion
          });
        } catch (e) {
          console.error('[PrivacyOptOut] Error verifying Shopify consent:', e);
        }
      }
    },

    /**
     * Dispatch custom event
     * @param {string} eventName - Event name
     * @param {Object} detail - Event detail
     */
    dispatchEvent(eventName, detail) {
      const event = new CustomEvent(eventName, {
        bubbles: true,
        detail: detail
      });
      document.dispatchEvent(event);
    },

    /**
     * Bind form handlers for privacy choices page
     */
    bindFormHandlers() {
      // Opt-out form handler
      const optOutForm = document.getElementById('privacy-opt-out-form');
      if (optOutForm) {
        optOutForm.addEventListener('submit', (e) => {
          e.preventDefault();
          this.handleOptOutSubmit(optOutForm);
        });

        // Pre-fill form with customer data if logged in
        this.prefillFormWithCustomerData(optOutForm);
      }

      // Opt-in button handler
      const optInBtn = document.getElementById('privacy-opt-in-btn');
      if (optInBtn) {
        optInBtn.addEventListener('click', (e) => {
          e.preventDefault();
          this.handleOptIn();
        });
      }

      // Update UI based on current status
      this.updatePrivacyUI();
    },

    /**
     * Pre-fill form fields with customer data if logged in
     * @param {HTMLFormElement} form
     */
    prefillFormWithCustomerData(form) {
      const customer = this.getCustomerInfo();
      if (!customer) return;

      // Pre-fill email field
      const emailField = form.querySelector('input[name="email"], #opt-out-email');
      if (emailField && customer.email && !emailField.value) {
        emailField.value = customer.email;
      }

      // Pre-fill name field
      const nameField = form.querySelector('input[name="name"], #opt-out-name');
      if (nameField && (customer.firstName || customer.lastName) && !nameField.value) {
        const fullName = [customer.firstName, customer.lastName].filter(Boolean).join(' ');
        nameField.value = fullName;
      }

      console.log('[PrivacyOptOut] Form pre-filled with customer data');
    },

    /**
     * Tag customer account via app proxy endpoint
     * @param {string} email - Customer email
     */
    tagCustomerAccount(email) {
      try {
        fetch(`/apps/dg-connection/check-opt-out?email=${encodeURIComponent(email)}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' }
        })
        .then(response => {
          if (response.ok) {
            console.log('[PrivacyOptOut] Customer account tagged via app proxy');
          } else {
            console.warn('[PrivacyOptOut] App proxy responded with status:', response.status);
          }
        })
        .catch(err => {
          console.error('[PrivacyOptOut] Error tagging customer account:', err);
        });
      } catch (e) {
        console.error('[PrivacyOptOut] Error calling app proxy:', e);
      }
    },

    /**
     * Handle opt-out form submission
     * @param {HTMLFormElement} form
     */
    handleOptOutSubmit(form) {
      const formData = new FormData(form);
      const email = formData.get('email');
      const name = formData.get('name');

      // Validate email
      if (!this.validateEmail(email)) {
        this.showMessage('error', 'Please enter a valid email address.');
        return;
      }

      // Set opt-out in localStorage and sync with Shopify
      const success = this.setOptOut(true, 'form');

      if (success) {
        // Store additional info for potential backend sync
        try {
          const requestData = {
            ...this.getOptOutData(),
            email: email,
            name: name || ''
          };
          localStorage.setItem(STORAGE_KEY + '_request', JSON.stringify(requestData));
        } catch (e) {
          console.error('[PrivacyOptOut] Error storing request data:', e);
        }

        // Tag customer account via app proxy
        this.tagCustomerAccount(email);

        // Log what was updated for verification
        console.log('[PrivacyOptOut] Opt-out submitted:', {
          email: email,
          localStorage: this.getOptOutData(),
          shopifyConsentSynced: typeof window.Shopify !== 'undefined' && !!window.Shopify.customerPrivacy
        });

        this.showMessage('success', 'Your opt-out preference has been saved. We will no longer sell or share your personal information for targeted advertising.');
        this.updatePrivacyUI();
      } else {
        this.showMessage('error', 'There was an error saving your preference. Please try again.');
      }
    },

    /**
     * Handle opt-in action
     */
    handleOptIn() {
      const success = this.clearOptOut();

      if (success) {
        this.showMessage('success', 'Your preference has been updated. You have opted back in to personalized advertising.');
        this.updatePrivacyUI();
      } else {
        this.showMessage('error', 'There was an error updating your preference. Please try again.');
      }
    },

    /**
     * Update privacy page UI based on current status
     */
    updatePrivacyUI() {
      const statusBanner = document.getElementById('privacy-status-banner');
      const optOutSection = document.getElementById('privacy-opt-out-section');
      const optInSection = document.getElementById('privacy-opt-in-section');
      const gpcNotice = document.getElementById('privacy-gpc-notice');

      // Build status message
      const customer = this.getCustomerInfo();
      const loggedInText = customer ? ` (Signed in as ${customer.email})` : '';

      if (statusBanner) {
        if (this.isOptedOut) {
          statusBanner.classList.add('opted-out');
          statusBanner.classList.remove('opted-in');
          statusBanner.innerHTML = `<p><strong>Status:</strong> You have opted out of the sale/sharing of your personal information.${loggedInText}</p>`;
        } else {
          statusBanner.classList.add('opted-in');
          statusBanner.classList.remove('opted-out');
          statusBanner.innerHTML = `<p><strong>Status:</strong> You have not opted out of the sale/sharing of your personal information.${loggedInText}</p>`;
        }
      }

      if (optOutSection) {
        optOutSection.style.display = this.isOptedOut ? 'none' : 'block';
      }

      if (optInSection) {
        optInSection.style.display = this.isOptedOut ? 'block' : 'none';
      }

      if (gpcNotice) {
        gpcNotice.style.display = this.hasGPCSignal ? 'block' : 'none';
        gpcNotice.setAttribute('aria-hidden', this.hasGPCSignal ? 'false' : 'true');
      }
    },

    /**
     * Show message to user
     * @param {string} type - Message type (success, error)
     * @param {string} message - Message text
     */
    showMessage(type, message) {
      const container = document.getElementById('privacy-message');
      if (container) {
        container.className = 'privacy-message privacy-message--' + type;
        container.textContent = message;
        container.style.display = 'block';
        container.setAttribute('role', 'alert');

        // Auto-hide after 5 seconds for success messages
        if (type === 'success') {
          setTimeout(() => {
            container.style.display = 'none';
          }, 5000);
        }
      }
    },

    /**
     * Validate email format
     * @param {string} email
     * @returns {boolean}
     */
    validateEmail(email) {
      const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      return re.test(String(email).toLowerCase());
    },

    /**
     * Open cookie consent modal (if available)
     */
    openCookieConsent() {
      let shown = false;

      // Try Shopify's privacyBanner.showPreferences() first (this is the correct method)
      if (typeof window.privacyBanner !== 'undefined' &&
          typeof window.privacyBanner.showPreferences === 'function') {
        window.privacyBanner.showPreferences();
        console.log('[PrivacyOptOut] Opened Shopify privacy banner preferences');
        shown = true;
      }
      // Fallback: Try window.Shopify.privacyBanner
      else if (typeof window.Shopify !== 'undefined' &&
               window.Shopify.privacyBanner &&
               typeof window.Shopify.privacyBanner.showPreferences === 'function') {
        window.Shopify.privacyBanner.showPreferences();
        console.log('[PrivacyOptOut] Opened Shopify.privacyBanner preferences');
        shown = true;
      }
      // Fallback: Try customerPrivacy.showPreferences if it exists
      else if (typeof window.Shopify !== 'undefined' &&
               window.Shopify.customerPrivacy &&
               typeof window.Shopify.customerPrivacy.showPreferences === 'function') {
        window.Shopify.customerPrivacy.showPreferences();
        console.log('[PrivacyOptOut] Opened customerPrivacy preferences');
        shown = true;
      }

      // Fallback to theme's custom cookie consent banner
      if (!shown) {
        const cookieConsent = document.querySelector('.cookie_consent');
        if (cookieConsent) {
          cookieConsent.style.display = 'block';
          console.log('[PrivacyOptOut] Opened theme cookie consent banner');
          shown = true;
        }
      }

      // If nothing available, log available objects for debugging
      if (!shown) {
        console.warn('[PrivacyOptOut] No cookie consent UI available. Checking what exists:');
        console.log('  - window.privacyBanner:', typeof window.privacyBanner);
        console.log('  - window.Shopify.privacyBanner:', typeof window.Shopify?.privacyBanner);
        console.log('  - window.Shopify.customerPrivacy:', typeof window.Shopify?.customerPrivacy);
        this.showMessage('error', 'Cookie preferences panel is not available. Please check your browser cookie settings or contact us for assistance.');
      }
    }
  };

  // Expose to global scope
  window.PrivacyOptOut = PrivacyOptOut;
})();
